/**
 * Thème métier Hublot.
 *
 * Le design system expose des emplacements CSS (--brand, --accent, --nav…) qui pointent sur
 * Poissonnerie par défaut et sur Boucherie quand <html data-activity="boucherie"> est posé
 * (voir src/assets/design-system/hublot.css). Vuetify, lui, lit ses couleurs dans son thème JS :
 * on aligne les deux ici, à partir des mêmes tokens, pour que « primary » et « --brand »
 * soient toujours la même couleur.
 *
 * Contexte du store : 'hublot' = Poissonnerie, 'carnot' = Boucherie.
 */

const ACTIVITY_BY_APP = { hublot: 'poissonnerie', carnot: 'boucherie' }

export function activityFromApp (app) {
  return ACTIVITY_BY_APP[app] || 'poissonnerie'
}

/** Valeur calculée d'un token (--brand, --accent…) une fois [data-activity] appliqué. */
export function token (name) {
  return getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim()
}

/**
 * Applique le métier : attribut sur <html> (CSS) + thème Vuetify (JS).
 * @param {import('vuetify/lib').Framework} vuetify  this.$vuetify
 * @param {'hublot'|'carnot'} app  contexte du store
 */
export function applyActivity (vuetify, app) {
  const activity = activityFromApp(app)
  document.documentElement.setAttribute('data-activity', activity)
  syncVuetifyColors(vuetify)
  return activity
}

/** Les deux thèmes Vuetify lisent les mêmes tokens : « primary » et --brand restent une seule couleur. */
function syncVuetifyColors (vuetify) {
  ['light', 'dark'].forEach(name => {
    const theme = vuetify.theme.themes[name]
    theme.primary = token('brand')
    theme.secondary = token('accent')
    theme.accent = token('accent')
    theme.info = token('brand')
    theme.success = token('success')
    theme.warning = token('warning')
    theme.error = token('danger')
  })
}

const THEME_KEY = 'crm-theme'

/** Thème mémorisé par l'utilisateur, sinon celui du système. */
export function initialTheme () {
  try {
    const saved = localStorage.getItem(THEME_KEY)
    if (saved === 'light' || saved === 'dark') return saved
  } catch (error) {
    // stockage local indisponible : on suit le système
  }
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Applique le thème clair ou sombre : attribut sur <html> (tokens.css) + mode sombre Vuetify.
 * @param {import('vuetify/lib').Framework} vuetify  this.$vuetify
 * @param {'light'|'dark'} theme
 */
export function applyTheme (vuetify, theme) {
  document.documentElement.setAttribute('data-theme', theme)
  vuetify.theme.dark = theme === 'dark'
  syncVuetifyColors(vuetify)
  try {
    localStorage.setItem(THEME_KEY, theme)
  } catch (error) {
    // stockage local indisponible : le choix ne survit pas au rechargement
  }
  return theme
}

/** Palette ECharts dans l'ordre chart-1 … chart-5 du design system. */
export function chartPalette () {
  return ['chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5'].map(token)
}
